import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Avatar,
  InputBase,
  IconButton,
  Paper,
  Badge,
  Menu,
  MenuItem,
  Divider,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import NotificationsOutlined from '@mui/icons-material/NotificationsOutlined';
import PublicIcon from '@mui/icons-material/Public';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LogoutIcon from '@mui/icons-material/Logout';
import { images } from 'common';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { fireStoreDB } from 'services';
import { getAuth, signOut } from 'firebase/auth';
import { getProfile } from 'utilities';
import { useNavigate } from 'react-router-dom';

// 🔧 Styled search box
const Search = styled('div')(() => ({
  position: 'relative',
  borderRadius: 20,
  backgroundColor: '#f5f6fa',
  marginLeft: 30,
  width: 330,
  height: 36,
}));

const StyledInputBase = styled(InputBase)(() => ({
  color: '#6a7c92',
  padding: '6px 12px',
  width: '100%',
  fontSize: 16,
}));

export default function Header({ businessData }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = async () => {
    handleMenuClose();
    try {
      const auth = getAuth();
      await signOut(auth);
      localStorage.clear(); // if you store tokens or profiles
      navigate('/sign-in');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const initials = (businessData?.companyName || 'W M')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('');

  return (
    <AppBar
      position='sticky'
      elevation={0}
      sx={{
        bgcolor: 'rgba(255,255,255,0.85)',
        backdropFilter: 'blur(8px)',
        borderBottom: '1px solid #EAECF0',
        color: '#101828',
        position: { xs: 'static', md: 'sticky' },
        top: 0,
        zIndex: (t) => t.zIndex.drawer + 1,
      }}
    >
      <Toolbar
        sx={{
          minHeight: { xs: 60, md: 68 },
          px: { xs: 2, md: 4 },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {/* Left — Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <img
            src={images.wmcLogo}
            alt='World Medical Card'
            style={{
              height: 44,
              width: 'auto',
              maxWidth: '100%',
              display: 'block',
              borderRadius: 0,
              objectFit: 'contain',
            }}
          />
        </Box>

        {/* Right — account menu */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            onClick={handleMenuOpen}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              cursor: 'pointer',
              pl: 1,
              pr: 1.25,
              py: 0.5,
              borderRadius: 2,
              border: '1px solid transparent',
              transition: 'all .15s ease',
              '&:hover': { bgcolor: '#F9FAFB', border: '1px solid #EAECF0' },
            }}
          >
            <Avatar
              src={businessData?.logo || undefined}
              sx={{
                width: 36,
                height: 36,
                bgcolor: '#32ABF8',
                color: '#fff',
                fontSize: 14,
                fontWeight: 700,
              }}
            >
              {initials}
            </Avatar>
            <Box sx={{ display: { xs: 'none', sm: 'block' }, lineHeight: 1.2 }}>
              <Typography
                sx={{ fontSize: 14, fontWeight: 600, color: '#101828', maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {businessData?.companyName || 'Company Name'}
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#667085' }}>
                Business account
              </Typography>
            </Box>
            <KeyboardArrowDownIcon sx={{ color: '#98A2B3', fontSize: 20 }} />
          </Box>

          {/* Dropdown Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              elevation: 0,
              sx: {
                mt: 1.5,
                borderRadius: 3,
                minWidth: 240,
                border: '1px solid #EAECF0',
                boxShadow: '0 8px 24px rgba(16,24,40,0.10)',
                overflow: 'hidden',
              },
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar
                src={businessData?.logo || undefined}
                sx={{ width: 40, height: 40, bgcolor: '#32ABF8', color: '#fff', fontSize: 15, fontWeight: 700 }}
              >
                {initials}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant='subtitle2' fontWeight={700} noWrap>
                  {businessData?.companyName || 'Company Name'}
                </Typography>
                <Typography variant='body2' color='text.secondary' noWrap>
                  {businessData?.email || 'No Email'}
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ borderColor: '#F2F4F7' }} />
            <MenuItem
              onClick={handleLogout}
              sx={{ color: '#D92D20', fontWeight: 600, fontSize: 14, py: 1.25, gap: 1.25 }}
            >
              <LogoutIcon sx={{ fontSize: 19 }} />
              Log out
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}
